// @ts-nocheck
import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { UserPlus, Upload, Pencil, Trash2, Info, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';
import { usStates } from '@/lib/constants';
import RosterAddPanel from '@/components/rosters/RosterAddPanel';
import PlayerEditForm from '@/components/rosters/PlayerEditForm';
import { MissingInfoIcons, ClaimControls } from '@/components/rosters/PlayerStatus';
import PlayerVerificationPanel from '@/components/rosters/PlayerVerificationPanel';
import CoachVerificationBanner from '@/components/rosters/CoachVerificationBanner';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

const GRAD_YEAR_OPTIONS = ['2026', '2027', '2028', '2029', '2030', '2031', '2032'];

interface Props {
  team: any;
  autoOpenAdd?: boolean;
}

function invokeError(error: any, data: any): string | null {
  if (error) return error.message ?? 'Request failed';
  if (data?.ok === false) return data.error?.message ?? 'Request failed';
  return null;
}

function initials(name: string) {
  return (name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
}

export default function TeamRosterView({ team, autoOpenAdd = false }: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [addTab, setAddTab] = useState<'search' | 'manual' | 'import'>('search');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [removing, setRemoving] = useState<any | null>(null);
  const [removeSubmitting, setRemoveSubmitting] = useState(false);

  const canEdit = !!user;
  const teamName = team.canonical_name ?? team.display_name ?? team.name ?? 'this team';

  // Nearest event registration — used only to fetch the master roster.
  const { data: registrations = [] } = useQuery({
    queryKey: ['team-registrations-list', team.id],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('v_team_registrations_summary')
        .select('event_registration_id, event_name, event_start_date, event_id')
        .eq('team_id', team.id)
        .order('event_start_date', { ascending: false });
      return data ?? [];
    },
  });

  const nearest = registrations[0];
  const eventRegistrationId = nearest?.event_registration_id ?? null;

  // Does the nearest event require player verification?
  const { data: verificationCtx } = useQuery({
    queryKey: ['event-verification-ctx', nearest?.event_id],
    enabled: !!nearest?.event_id,
    queryFn: async () => {
      const [{ data: settings }, { data: ev }] = await Promise.all([
        (supabase as any).rpc('get_event_verification_settings', { p_event_id: nearest.event_id }),
        (supabase as any).from('events').select('organization_id').eq('id', nearest.event_id).maybeSingle(),
      ]);
      return { settings: settings ?? null, orgId: ev?.organization_id ?? null };
    },
  });
  const verification = verificationCtx?.settings;
  const verificationOrgId = verificationCtx?.orgId;


  useEffect(() => {
    if (autoOpenAdd && eventRegistrationId) setShowAddPanel(true);
  }, [autoOpenAdd, eventRegistrationId]);

  const { data: rosterData, isLoading } = useQuery({
    queryKey: ['team-roster', team.id],
    enabled: !!team.id,
    queryFn: async () => {
      const res = await invokeEdgeFunction('rle-list-team-roster', {
        body: { team_id: team.id },
      });
      const err = invokeError(res.error, res.data);
      if (err) throw new Error(err);
      return res.data?.data ?? res.data;
    },
  });

  const master = rosterData?.master ?? [];
  const guests = rosterData?.guests ?? [];
  const masterCount = rosterData?.master_count ?? master.length;

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['team-roster', team.id] });

  const handleRemove = async () => {
    if (!removing) return;
    setRemoveSubmitting(true);
    try {
      const { data, error } = await invokeEdgeFunction('rle-remove-player-from-roster', {
        body: { roster_entry_id: removing.roster_entry_id ?? removing.player_id, player_id: removing.player_id },
      });
      const err = invokeError(error, data);
      if (err) throw new Error(err);
      toast.success('Player removed from roster.');
      setRemoving(null);
      refresh();
    } catch (e: any) {
      toast.error(e.message ?? 'Failed to remove player');
    } finally {
      setRemoveSubmitting(false);
    }
  };

  if (registrations.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          No registrations yet. Register for an event to build a roster.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {nearest?.event_id && <CoachVerificationBanner eventId={nearest.event_id} />}

      <Alert className="bg-cyan-50/60 dark:bg-cyan-950/20 border-cyan-200 dark:border-cyan-800 text-cyan-900 dark:text-cyan-100 [&>svg]:text-cyan-600 dark:[&>svg]:text-cyan-400">
        <Info className="h-4 w-4" />
        <AlertDescription>
          This is your team&apos;s full roster — every player on {teamName}. Add each player once here.
          You&apos;ll choose who plays at each tournament under the Scheduled events tab.
        </AlertDescription>
      </Alert>

      {showAddPanel && (
        eventRegistrationId ? (
          <RosterAddPanel
            eventRegistrationId={eventRegistrationId}
            eventId={nearest?.event_id}
            teamId={team.id}
            teamName={teamName}
            defaultTab={addTab}
            onClose={() => setShowAddPanel(false)}
            onRosterChanged={refresh}
          />
        ) : (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Players are added against an event registration and this team doesn&apos;t have one we can
              use yet. Register {teamName} for an event, then add players here.
            </AlertDescription>
          </Alert>
        )
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
          <CardTitle>Roster · {masterCount} player{masterCount === 1 ? '' : 's'}</CardTitle>
          {canEdit && (
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" onClick={() => { setAddTab('manual'); setShowAddPanel(true); }}>
                <UserPlus className="mr-1 h-4 w-4" />
                Add player
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setAddTab('search'); setShowAddPanel(true); }}>
                Search existing
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setAddTab('import'); setShowAddPanel(true); }}>
                <Upload className="mr-1 h-4 w-4" />
                Import (AI)
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : master.length === 0 && guests.length === 0 ? (
            <div className="text-center py-8 space-y-3">
              <p className="text-muted-foreground">No players yet. Add players to build your team roster.</p>
              {canEdit && (
                <Button size="sm" onClick={() => setShowAddPanel(true)}>
                  <UserPlus className="mr-1 h-4 w-4" />
                  Add player
                </Button>
              )}
            </div>
          ) : (
            <TooltipProvider>
              {(() => {
                const renderRow = (p: any, opts: { isGuest?: boolean } = {}) => {
                  const name = p.full_name ?? p.name ?? '';
                  if (editingId === p.player_id) {
                    return (
                      <li key={p.player_id} className="py-3">
                        <PlayerEditForm
                          player={p}
                          teamId={team.id}
                          onCancel={() => setEditingId(null)}
                          onSaved={() => { setEditingId(null); refresh(); }}
                        />
                      </li>
                    );
                  }
                  return (
                    <li key={p.player_id} className="bg-white even:bg-slate-50 hover:bg-slate-100 px-2 py-2.5">
                    <div className="flex items-center gap-3">

                      <Avatar className="h-9 w-9">
                        <AvatarFallback className="text-xs">{initials(name)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="font-medium truncate flex items-center gap-2">
                          {name}
                          {opts.isGuest && (
                            <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 border-yellow-200">Guest</Badge>
                          )}
                          <MissingInfoIcons missing={p.missing} />
                        </p>
                        {opts.isGuest && p.home_team_name && (
                          <p className="text-xs text-muted-foreground">from {p.home_team_name}</p>
                        )}
                        {(() => {
                          const detail = [
                            p.grad_year ? `'${String(p.grad_year).slice(-2)}` : null,
                            p.position || null,
                            p.jersey_number ? `#${p.jersey_number}` : null,
                          ].filter(Boolean).join(' · ');
                          return detail ? (
                            <p className="text-xs text-muted-foreground">{detail}</p>
                          ) : null;
                        })()}
                      </div>
                      {canEdit && (
                        <div className="ml-auto flex items-center gap-2">
                          <ClaimControls player={p} onChanged={refresh} />
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={() => setEditingId(p.player_id)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Edit</TooltipContent>
                          </Tooltip>
                          {!opts.isGuest && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-destructive hover:text-destructive"
                                  onClick={() => setRemoving(p)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Remove</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      )}
                      </div>
                      {verification?.enabled && verificationOrgId && (
                        <div className="mt-2">
                          <PlayerVerificationPanel
                            playerId={p.player_id}
                            orgId={verificationOrgId}
                            requireGrade={!!verification?.require_grade}
                            compact
                          />
                        </div>
                      )}
                    </li>
                  );

                };
                return (
                  <>
                    {master.length > 0 && (
                      <ul className="divide-y">{master.map((p: any) => renderRow(p))}</ul>
                    )}
                    {guests.length > 0 && (
                      <div className="mt-6">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                          Guests
                        </p>
                        <ul className="divide-y">{guests.map((p: any) => renderRow(p, { isGuest: true }))}</ul>
                      </div>
                    )}
                  </>
                );
              })()}
            </TooltipProvider>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removing?.full_name ?? removing?.name} from the roster?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the player from your team's permanent roster.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeSubmitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); handleRemove(); }} disabled={removeSubmitting}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

